import { StrictMode, type ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { AppRoutes } from '@/routes/AppRoutes';
import { Toaster } from '@/components/design-system/toast';
import { NeonAuthProvider } from '@/components/platform/NeonAuthProvider';
import { ErrorBoundary } from '@/components/platform/ErrorBoundary';
import { getQueryClient } from '@/utils';

interface AppProps {
  /**
   * Router component - either StaticRouter (server) or BrowserRouter (client)
   */
  router: ReactNode;
  /**
   * Optional HelmetProvider context for SSR
   */
  helmetContext?: object;
}

/**
 * App Component
 *
 * This is the shared application tree used by both client and server entries.
 * Keeping this in one place ensures the component structure always matches
 * between SSR and hydration, preventing hydration mismatches.
 *
 * The only difference between server and client rendering is the Router
 * component (StaticRouter vs BrowserRouter), which is passed as a prop.
 */
export function App({ router, helmetContext }: AppProps) {
  const queryClient = getQueryClient();

  return (
    <StrictMode>
      <ErrorBoundary>
        <NeonAuthProvider>
          <QueryClientProvider client={queryClient}>
            <HelmetProvider context={helmetContext}>{router}</HelmetProvider>
          </QueryClientProvider>
        </NeonAuthProvider>
      </ErrorBoundary>
    </StrictMode>
  );
}

/**
 * AppContent Component
 *
 * The actual application content (routes + toaster).
 * This is rendered inside the router, which is passed as a prop to App.
 */
export function AppContent() {
  return (
    <>
      <AppRoutes />
      <Toaster />
    </>
  );
}
